import { KSTransaction } from './types';

// Periodicity types
export type PeriodicityType =
  | 'daily'
  | 'weekly'
  | 'biweekly'
  | 'monthly'
  | 'quarterly'
  | 'annual'
  | 'irregular'
  | 'unknown'
  | 'regular';

interface TemporalPattern {
  avgDaysBetween: number;
  stdDevDaysBetween: number;
  typicalDaysOfWeek: Set<number>;
  typicalDaysOfMonth: Set<number>;
  typicalMonthlyFrequency: number;
  periodicity: PeriodicityType;
  periodicityConfidence: number;
}

interface AnomalyResult {
  transaction: KSTransaction;
  anomalyTypes: string[];
  confidence: number;
  expectedPattern?: any;
  actualPattern?: any;
  periodicity?: PeriodicityType;
}

const MIN_HISTORY_LENGTH = 3;
const CONFIDENCE_THRESHOLD = 0.8;

/**
 * Analyzes temporal patterns in historical transactions
 */
export function analyzeTemporalPattern(transactions: KSTransaction[]): TemporalPattern {
  if (transactions.length < MIN_HISTORY_LENGTH) {
    throw new Error('Insufficient transaction history for pattern analysis');
  }

  // Sort transactions by date
  const sortedTxns = [...transactions].sort((a, b) => a.date.getTime() - b.date.getTime());

  // Calculate days between transactions
  const daysBetween = sortedTxns.slice(1).map((txn, i) => {
    return (txn.date.getTime() - sortedTxns[i].date.getTime()) / (1000 * 60 * 60 * 24);
  });

  // Calculate average and standard deviation of days between transactions
  const avgDaysBetween = daysBetween.reduce((sum, days) => sum + days, 0) / daysBetween.length;
  const stdDevDaysBetween = Math.sqrt(
    daysBetween.reduce((sum, days) => sum + Math.pow(days - avgDaysBetween, 2), 0) / daysBetween.length
  );

  // Analyze typical days of week and month
  const daysOfWeek = new Set(sortedTxns.map(txn => txn.date.getDay()));
  const daysOfMonth = new Set(sortedTxns.map(txn => txn.date.getDate()));

  // Calculate typical monthly frequency
  const months = new Set(sortedTxns.map(txn => txn.date.getMonth() + txn.date.getFullYear() * 12)).size;
  const typicalMonthlyFrequency = transactions.length / months;

  // Determine periodicity and confidence
  const { periodicity, confidence } = detectPeriodicity(sortedTxns, daysBetween, avgDaysBetween, stdDevDaysBetween);

  return {
    avgDaysBetween,
    stdDevDaysBetween,
    typicalDaysOfWeek: daysOfWeek,
    typicalDaysOfMonth: daysOfMonth,
    typicalMonthlyFrequency,
    periodicity,
    periodicityConfidence: confidence,
  };
}

/**
 * Detects the periodicity pattern in transactions
 */
function detectPeriodicity(
  sortedTxns: KSTransaction[],
  daysBetween: number[],
  avgDaysBetween: number,
  stdDevDaysBetween: number
): { periodicity: PeriodicityType; confidence: number } {
  // Not enough data for reliable detection
  if (sortedTxns.length < 3) {
    return { periodicity: 'unknown', confidence: 0 };
  }

  // Calculate coefficient of variation to measure consistency
  const cv = stdDevDaysBetween / avgDaysBetween;

  // If transactions are very irregular, return early
  if (cv > 0.5) {
    return { periodicity: 'irregular', confidence: Math.min(1, cv) };
  }

  // Check for common patterns
  const isConsistent = cv < 0.3; // Low variation indicates consistent pattern

  // Analyze days between transactions to detect patterns
  if (isConsistent) {
    // Daily pattern (0-2 days between)
    if (avgDaysBetween <= 2) {
      return { periodicity: 'daily', confidence: 0.9 };
    }

    // Weekly pattern (6-8 days between)
    if (avgDaysBetween >= 6 && avgDaysBetween <= 8) {
      return { periodicity: 'weekly', confidence: 0.9 };
    }

    // Biweekly pattern (13-16 days between)
    if (avgDaysBetween >= 13 && avgDaysBetween <= 16) {
      return { periodicity: 'biweekly', confidence: 0.85 };
    }

    // Monthly pattern (28-32 days between)
    if (avgDaysBetween >= 28 && avgDaysBetween <= 32) {
      return { periodicity: 'monthly', confidence: 0.9 };
    }

    // Quarterly pattern (85-95 days between)
    if (avgDaysBetween >= 85 && avgDaysBetween <= 95) {
      return { periodicity: 'quarterly', confidence: 0.85 };
    }

    // Annual pattern (350-380 days between)
    if (avgDaysBetween >= 350 && avgDaysBetween <= 380) {
      return { periodicity: 'annual', confidence: 0.9 };
    }
  }

  // Check for same day of month pattern (monthly)
  const daysOfMonth = sortedTxns.map(txn => txn.date.getDate());
  const uniqueDaysOfMonth = new Set(daysOfMonth);

  if (uniqueDaysOfMonth.size <= 2 && avgDaysBetween > 20) {
    return { periodicity: 'monthly', confidence: 0.8 };
  }

  // Check for same day of week pattern (weekly)
  const daysOfWeek = sortedTxns.map(txn => txn.date.getDay());
  const uniqueDaysOfWeek = new Set(daysOfWeek);

  if (uniqueDaysOfWeek.size <= 2 && avgDaysBetween > 5 && avgDaysBetween < 9) {
    return { periodicity: 'weekly', confidence: 0.8 };
  }

  // If we can't determine a specific pattern but transactions are regular
  if (isConsistent) {
    return { periodicity: 'regular', confidence: 0.6 };
  }

  return { periodicity: 'irregular', confidence: 0.5 };
}

/**
 * Detects sequence anomalies in transactions
 */
function detectSequenceAnomalies(
  transaction: KSTransaction,
  pattern: TemporalPattern,
  previousTxn: KSTransaction | null
): string[] {
  const anomalies: string[] = [];

  // Check if transaction falls on unusual day of week
  if (!pattern.typicalDaysOfWeek.has(transaction.date.getDay())) {
    anomalies.push('unusual_day_of_week');
  }

  // Check if transaction falls on unusual day of month
  if (!pattern.typicalDaysOfMonth.has(transaction.date.getDate())) {
    anomalies.push('unusual_day_of_month');
  }

  // Check for unusual gap if we have a previous transaction
  if (previousTxn) {
    const daysBetween = (transaction.date.getTime() - previousTxn.date.getTime()) / (1000 * 60 * 60 * 24);
    const zScore = Math.abs(daysBetween - pattern.avgDaysBetween) / pattern.stdDevDaysBetween;

    if (zScore > 2) {
      // More than 2 standard deviations
      anomalies.push(daysBetween > pattern.avgDaysBetween ? 'unusual_long_gap' : 'unusual_short_gap');
    }
  }

  return anomalies;
}

/**
 * Groups transactions by month
 */
function groupTransactionsByMonth(transactions: KSTransaction[]): Map<string, KSTransaction[]> {
  return transactions.reduce((groups, txn) => {
    const monthKey = `${txn.date.getFullYear()}-${txn.date.getMonth()}`;
    if (!groups.has(monthKey)) {
      groups.set(monthKey, []);
    }
    groups.get(monthKey)!.push(txn);
    return groups;
  }, new Map<string, KSTransaction[]>());
}

/**
 * Detects frequency anomalies by analyzing monthly patterns
 */
function detectFrequencyAnomalies(transactions: KSTransaction[], pattern: TemporalPattern): AnomalyResult[] {
  const anomalies: AnomalyResult[] = [];
  const monthlyGroups = groupTransactionsByMonth(transactions);

  // Analyze each month's frequency
  monthlyGroups.forEach((monthTxns, monthKey) => {
    const frequencyDiff = Math.abs(monthTxns.length - pattern.typicalMonthlyFrequency);
    const normalizedDiff = frequencyDiff / pattern.typicalMonthlyFrequency;

    if (normalizedDiff > 0.5) {
      // More than 50% difference from typical frequency
      monthTxns.forEach(txn => {
        anomalies.push({
          transaction: txn,
          anomalyTypes: ['unusual_monthly_frequency'],
          confidence: Math.min(normalizedDiff, 1),
          expectedPattern: { frequency: pattern.typicalMonthlyFrequency },
          actualPattern: { frequency: monthTxns.length },
        });
      });
    }
  });

  return anomalies;
}

/**
 * Main function to detect temporal anomalies in a set of transactions
 */
export function detectTemporalAnomalies(transactions: KSTransaction[]): AnomalyResult[] {
  const pattern = analyzeTemporalPattern(transactions);
  const anomalies: AnomalyResult[] = [];

  // Sort transactions by date
  const sortedTxns = [...transactions].sort((a, b) => a.date.getTime() - b.date.getTime());

  // Detect sequence anomalies
  sortedTxns.forEach((txn, i) => {
    const previousTxn = i > 0 ? sortedTxns[i - 1] : null;
    const sequenceAnomalies = detectSequenceAnomalies(txn, pattern, previousTxn);

    if (sequenceAnomalies.length > 0) {
      anomalies.push({
        transaction: txn,
        anomalyTypes: sequenceAnomalies,
        confidence: 0.9,
        expectedPattern: pattern,
        actualPattern: {
          dayOfWeek: txn.date.getDay(),
          dayOfMonth: txn.date.getDate(),
          daysSincePrevious: previousTxn
            ? (txn.date.getTime() - previousTxn.date.getTime()) / (1000 * 60 * 60 * 24)
            : null,
        },
        periodicity: pattern.periodicity,
      });
    }
  });

  // Add frequency anomalies
  anomalies.push(...detectFrequencyAnomalies(transactions, pattern));

  // Detect periodicity-specific anomalies
  const periodicityAnomalies = detectPeriodicityAnomalies(sortedTxns, pattern);
  anomalies.push(...periodicityAnomalies);

  return anomalies;
}

/**
 * Detects anomalies specific to the identified periodicity pattern
 */
function detectPeriodicityAnomalies(transactions: KSTransaction[], pattern: TemporalPattern): AnomalyResult[] {
  const anomalies: AnomalyResult[] = [];

  // Only process if we have a clear periodicity with good confidence
  if (pattern.periodicityConfidence < 0.7 || pattern.periodicity === 'irregular' || pattern.periodicity === 'unknown') {
    return anomalies;
  }

  const sortedTxns = [...transactions].sort((a, b) => a.date.getTime() - b.date.getTime());

  // For monthly patterns, check if transactions occur on consistent day of month
  if (pattern.periodicity === 'monthly') {
    const expectedDaysOfMonth = Array.from(pattern.typicalDaysOfMonth);

    // If we have a consistent day of month
    if (expectedDaysOfMonth.length <= 2) {
      sortedTxns.forEach(txn => {
        const dayOfMonth = txn.date.getDate();
        if (!pattern.typicalDaysOfMonth.has(dayOfMonth)) {
          anomalies.push({
            transaction: txn,
            anomalyTypes: ['inconsistent_monthly_day'],
            confidence: 0.85,
            expectedPattern: { daysOfMonth: expectedDaysOfMonth },
            actualPattern: { dayOfMonth },
            periodicity: pattern.periodicity,
          });
        }
      });
    }
  }

  // For weekly patterns, check if transactions occur on consistent day of week
  if (pattern.periodicity === 'weekly') {
    const expectedDaysOfWeek = Array.from(pattern.typicalDaysOfWeek);

    // If we have a consistent day of week
    if (expectedDaysOfWeek.length <= 2) {
      sortedTxns.forEach(txn => {
        const dayOfWeek = txn.date.getDay();
        if (!pattern.typicalDaysOfWeek.has(dayOfWeek)) {
          anomalies.push({
            transaction: txn,
            anomalyTypes: ['inconsistent_weekly_day'],
            confidence: 0.85,
            expectedPattern: { daysOfWeek: expectedDaysOfWeek },
            actualPattern: { dayOfWeek },
            periodicity: pattern.periodicity,
          });
        }
      });
    }
  }

  // Check for missing transactions in the sequence based on periodicity
  const missingTransactions = detectMissingTransactions(sortedTxns, pattern);
  if (missingTransactions.length > 0) {
    // We can't add these directly to anomalies since they're not tied to existing transactions
    // But we could handle them separately in future implementations
    // Logging disabled for production
    // console.log('Missing transactions detected:', missingTransactions);
  }

  return anomalies;
}

/**
 * Detects potentially missing transactions based on the periodicity pattern
 */
function detectMissingTransactions(transactions: KSTransaction[], pattern: TemporalPattern): Date[] {
  const missingDates: Date[] = [];

  // Only check for missing transactions if we have a clear periodicity
  if (
    pattern.periodicityConfidence < 0.8 ||
    pattern.periodicity === 'irregular' ||
    pattern.periodicity === 'unknown' ||
    transactions.length < 3
  ) {
    return missingDates;
  }

  const sortedTxns = [...transactions].sort((a, b) => a.date.getTime() - b.date.getTime());
  const firstDate = sortedTxns[0].date;
  const lastDate = sortedTxns[sortedTxns.length - 1].date;

  // Define expected interval based on periodicity
  const expectedIntervalDays = pattern.avgDaysBetween;

  // For each pair of consecutive transactions, check if there are missing transactions between them
  for (let i = 0; i < sortedTxns.length - 1; i++) {
    const currentDate = sortedTxns[i].date;
    const nextDate = sortedTxns[i + 1].date;

    const daysBetween = (nextDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24);

    // If the gap is significantly larger than expected (more than 1.5x the expected interval)
    if (daysBetween > expectedIntervalDays * 1.5) {
      // Calculate how many transactions should be in this gap
      const expectedTransactions = Math.round(daysBetween / expectedIntervalDays) - 1;

      // Add expected missing transaction dates
      for (let j = 1; j <= expectedTransactions; j++) {
        const missingDate = new Date(currentDate.getTime() + j * expectedIntervalDays * 24 * 60 * 60 * 1000);
        missingDates.push(missingDate);
      }
    }
  }

  return missingDates;
}

/**
 * Detects transactions on holidays and weekends
 */
function detectNonBusinessDayTransactions(transactions: KSTransaction[], holidays: Date[] = []): AnomalyResult[] {
  const holidayTimestamps = new Set(holidays.map(d => d.getTime()));

  return transactions.reduce((anomalies: AnomalyResult[], txn) => {
    const dayOfWeek = txn.date.getDay();
    const isHoliday = holidayTimestamps.has(txn.date.getTime());
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

    if (isHoliday || isWeekend) {
      anomalies.push({
        transaction: txn,
        anomalyTypes: [...(isHoliday ? ['holiday_transaction'] : []), ...(isWeekend ? ['weekend_transaction'] : [])],
        confidence: 0.95,
      });
    }

    return anomalies;
  }, []);
}

/* // Detect temporal anomalies
const temporalAnomalies = detectTemporalAnomalies(sampleTransactions);

// Define holidays and check for non-business day transactions
const holidays = [
  new Date('2024-01-01'), // New Year's Day
  new Date('2024-12-25'), // Christmas
  // Add more holidays as needed
];
const nonBusinessDayAnomalies = detectNonBusinessDayTransactions(sampleTransactions, holidays);

// Combine all anomalies
const allAnomalies = [...temporalAnomalies, ...nonBusinessDayAnomalies]; */
